import { Avatar, Modal, Switch, Select, message } from "antd";
import { Button } from "../../components/Component";
import {
  CompressOutlined,
  ExpandOutlined,
  SunOutlined,
  MoonOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import "./CommentsTab.css";
import ProjectInDetailModal from "../../pages/ProjectScheduleManagement/projects/ProjectInDetailModal";
import { io, Socket } from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import {
  initializeSocket,
  getSocket,
  disconnectSocket,
  joinRoom,
  leaveRoom,
} from "./socketService";
import Cookies from "js-cookie";
import { SelectOption } from "../../pages/form-components/SelectComponent";
import { projectsApis } from "../../api/projects";
import { tasksApis } from "../../api/NewTasks";
import { commentsApis } from "../../api/Comments";
import { useLocation, useNavigate } from "react-router-dom";
import {
  updateUserDetailModalSlice,
  resetUserDetailModalSlice,
} from "../../store/slices/projectManagement/user-detail-view/UserDetailModalSlice";
import { RootState } from "../../store/store";
import Swal from "sweetalert2";
import { useMentions } from "./useMentions";

// Types and interfaces
type CommentEntityType = "Project" | "Phase" | "Activity" | "Task" | "Issue";
type CommentStatus = "Draft" | "Sent";
type EditorMode = "main" | "reply" | "edit" | "modal";

interface TaskOption extends SelectOption {
  entityType: CommentEntityType;
}

interface ProjectUser {
  userId: string;
  userName: string;
  userDetailId: number;
  projectUserId: number;
  roleName: string;
}

interface CommentsTabProps {
  name: string;
  module: CommentEntityType;
  moduleId: number;
  projectId: number;
  projectName: string;
  showTaskModal?: boolean;
  isFromWorkUI?: boolean;
}

export interface HashTagRequest {
  hashtag: string;
  entityType: CommentEntityType;
  entityId?: number;
}

export interface MentionUserRequest {
  userId: number;
  userName: string;
  userDetailId: number;
}

interface NewCommentRequest {
  projectId: number;
  message: string;
  module: CommentEntityType;
  id: number;
  status?: CommentStatus;
  parentId?: number;
  attachments?: any[];
  hashtags?: Array<HashTagRequest>;
  mentions?: Array<MentionUserRequest>;
  projectName: string;
  taskName?: string;
  issueName?: string;
}

export interface Comment {
  id: number;
  text: string;
  timestamp: string;
  user: string;
  initials: string;
  files: any[];
  socketId?: string;
  hashtags?: Array<HashTagRequest>;
  mentions?: Array<MentionUserRequest>;
  parentId?: number;
  replies?: Comment[];
}

interface DeleteCommentPayload {
  commentId: number;
  module: CommentEntityType;
  id: number;
  projectId: number;
  projectName: string;
  taskName?: string;
  issueName?: string;
}

interface EditorState {
  value: string;
  attachedFiles: any[];
  hashtags: HashTagRequest[];
  mentions: MentionUserRequest[];
  showDropdown: boolean;
  dropdownType: "mention" | "task" | null;
  searchTerm: string;
  dropdownPosition: { top: number; left: number };
}

const getInitials = (name: string) => {
  if (!name || typeof name !== "string") return "";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

const hasEditorContent = (html: string) => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim().length > 0;
};

export const CommentsTab: React.FC<CommentsTabProps> = ({
  name,
  module,
  moduleId,
  projectId,
  projectName,
  showTaskModal = true,
  isFromWorkUI = false,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { id, derivedProjectStatus } = location.state || {};
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal
  );
  const { data: UserDetailModal } = useSelector(
    (state: RootState) => state.UserDetailModalSlice
  );

  // Core state
  const [comments, setComments] = useState<Comment[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [isLoading, setIsLoading] = useState(true);

  // UI state
  const [expanded, setExpanded] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [hoveredCommentId, setHoveredCommentId] = useState<number | null>(null);

  // Editor states - separated for clarity
  const [mainEditor, setMainEditor] = useState<EditorState>({
    value: "",
    attachedFiles: [],
    hashtags: [],
    mentions: [],
    showDropdown: false,
    dropdownType: null,
    searchTerm: "",
    dropdownPosition: { top: 0, left: 0 },
  });

  const [replyEditor, setReplyEditor] = useState<
    EditorState & { parentId: number | null }
  >({
    value: "",
    attachedFiles: [],
    hashtags: [],
    mentions: [],
    showDropdown: false,
    dropdownType: null,
    searchTerm: "",
    dropdownPosition: { top: 0, left: 0 },
    parentId: null,
  });

  const [editEditor, setEditEditor] = useState<
    EditorState & { commentId: number | null; originalComment: Comment | null }
  >({
    value: "",
    attachedFiles: [],
    hashtags: [],
    mentions: [],
    showDropdown: false,
    dropdownType: null,
    searchTerm: "",
    dropdownPosition: { top: 0, left: 0 },
    commentId: null,
    originalComment: null,
  });

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [confirmDeleteChecked, setConfirmDeleteChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Data state
  const [projectUsers, setProjectUsers] = useState<any[]>([]);
  const [taskDropdownData, setTaskDropdownData] = useState<TaskOption[]>([]);

  // Mentions processing hook
  const {
    processMessageForSocket,
    processMessageForDisplay,
    synchronizeWithText,
    addMention,
    addHashtag,
    getFilteredMentions,
    getFilteredTasks,
  } = useMentions({ projectUsers, taskDropdownData });

  // Socket state - using refs to prevent re-renders
  const socketRef = useRef<Socket | null>(null);
  const isConnectedRef = useRef(false);
  const roomJoinedRef = useRef(false);
  const eventHandlersRef = useRef<{
    handleNewComment?: (data: any) => void;
    handleEditComment?: (data: any) => void;
    handleDeleteComment?: (data: any) => void;
  }>({});
  const [socketStatus, setSocketStatus] = useState({
    connected: false,
    initialized: false,
  });

  // Stable refs to prevent effect dependencies
  const moduleRef = useRef(module);
  const moduleIdRef = useRef(moduleId);
  const currentRoomRef = useRef<string | null>(null);

  // Refs
  const quillMainRef = useRef<any>(null);
  const quillReplyRef = useRef<any>(null);
  const quillEditRef = useRef<any>(null);
  const dropdownDivRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLUListElement>(null);
  const mountedRef = useRef(true);

  // Update refs when props change
  useEffect(() => {
    moduleRef.current = module;
    moduleIdRef.current = moduleId;
  }, [module, moduleId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Memoized toolbar options
  const toolbarOptions = useMemo(
    () =>
      isFromWorkUI
        ? []
        : [
            [{ font: [] }],
            ["bold", "italic", "underline", "strike"],
            ["blockquote", "code-block"],
            [{ header: 1 }, { header: 2 }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ direction: "rtl" }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ["link", "image", "video"],
            ["clean"],
          ],
    [isFromWorkUI]
  );

  // Helper functions
  const createEditorState = useCallback(
    (): EditorState => ({
      value: "",
      attachedFiles: [],
      hashtags: [],
      mentions: [],
      showDropdown: false,
      dropdownType: null,
      searchTerm: "",
      dropdownPosition: { top: 0, left: 0 },
    }),
    []
  );


  const resetMainEditor = useCallback(() => {
    setMainEditor(createEditorState());
  }, [createEditorState]);

  const resetReplyEditor = useCallback(() => {
    setReplyEditor({
      ...createEditorState(),
      parentId: null,
    });
  }, [createEditorState]);

  const resetEditEditor = useCallback(() => {
    setEditEditor({
      ...createEditorState(),
      commentId: null,
      originalComment: null,
    });
  }, [createEditorState]);

  // Memoized task lookup for better performance
  const taskLookupMap = useMemo(() => {
    const map = new Map();
    taskDropdownData.forEach(task => {
      map.set(task.value, task.label);
    });
    return map;
  }, [taskDropdownData]);

  // Format functions with stable dependencies
  const formatSocketComment = useCallback(
    (socketComment: any): Comment => {
      const mentions = socketComment.mentions?.map((m: any) => ({
        userId: Number(m.userId),
        userName: m.userName || `User ${m.userId}`,
        userDetailId: Number(m.userDetailId),
      })) || [];

      // Process message to convert @userId back to @UserName for display
      const displayMessage = processMessageForDisplay(socketComment.message || socketComment.text || "", mentions);

      return {
        id: socketComment.commentId || socketComment.id || Date.now(),
        text: displayMessage, // Use processed message for display
        timestamp: socketComment.createdAt
          ? moment(socketComment.createdAt).format("MM-DD-YYYY hh:mm a")
          : moment().format("MM-DD-YYYY hh:mm a"),
        user: socketComment.commentedBy || "Unknown User",
        initials: getInitials(socketComment.commentedBy || "UU"),
        files: socketComment.attachments || socketComment.files || [],
        socketId: socketComment.socketId,
        hashtags:
          socketComment.hashtags?.map((h: any) => ({
            hashtag: taskLookupMap.get(Number(h.entityId)) || `Task${h.entityId}`,
            entityType: h.entityType || "Task",
            entityId: Number(h.entityId) || undefined,
          })) || [],
        mentions: mentions,
        parentId: socketComment.parentId,
      };
    },
    [taskLookupMap, processMessageForDisplay]
  );

  const formatApiComment = useCallback(
    (comment: any, parentId?: number): Comment => {
      const mentions = comment.mentions?.map((m: any) => ({
        userId: Number(m.userId),
        userName: m.userName || `User ${m.userId}`,
        userDetailId: Number(m.userDetailId),
      })) || [];

      // Process the comment content to convert @userId back to @UserName for display
      const displayText = processMessageForDisplay(comment.content, mentions);

      return {
        id: comment.commentId,
        text: displayText, // Use processed text for display
        timestamp: moment(comment.commentedAt).format("MM-DD-YYYY hh:mm a"),
        user: comment.commentedBy,
        initials: getInitials(comment.commentedBy),
        files: [],
        hashtags:
          comment.hashtags?.map((h: any) => ({
            ...h,
            hashtag: taskLookupMap.get(Number(h.entityId)) || `Task${h.entityId}`,
            entityType: h.entityType || "Task",
            entityId: Number(h.entityId) || undefined,
          })) || [],
        mentions: mentions,
        parentId,
      };
    },
    [taskLookupMap, processMessageForDisplay]
  );

  const flattenComments = useCallback(
    (commentsData: any[], parentId?: number): Comment[] => {
      let flattened: Comment[] = [];
      commentsData.forEach((comment) => {
        const formattedComment = formatApiComment(comment, parentId);
        if (comment.replies && comment.replies.length > 0) {
          formattedComment.replies = flattenComments(
            comment.replies,
            comment.commentId
          );
        }
        flattened.push(formattedComment);
      });
      return flattened;
    },
    [formatApiComment]
  );

  // Stable socket event handlers
  const handleNewComment = useCallback(
    (newComment: any) => {
      if (!mountedRef.current) return;

      if (
        newComment.module === moduleRef.current &&
        newComment.id === moduleIdRef.current
      ) {
        if (newComment.parentId) {
          setComments((prev) =>
            prev.map((comment) => {
              if (comment.id === newComment.parentId) {
                return {
                  ...comment,
                  replies: [
                    ...(comment.replies || []),
                    formatSocketComment(newComment),
                  ],
                };
              }
              return comment;
            })
          );
        } else {
          setComments((prev) => [formatSocketComment(newComment), ...prev]);
        }
      }
    },
    [formatSocketComment]
  );

  const handleEditComment = useCallback((updatedComment: any) => {
    if (!mountedRef.current) return;

    if (
      updatedComment.module === moduleRef.current &&
      updatedComment.id === moduleIdRef.current
    ) {
      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id === updatedComment.commentId) {
            const mentions = updatedComment.mentions || comment.mentions;
            const displayMessage = processMessageForDisplay(updatedComment.message || comment.text, mentions);
            return {
              ...comment,
              text: displayMessage, // Convert @id back to @UserName for display
              hashtags: updatedComment.hashtags || comment.hashtags,
              mentions: mentions,
              timestamp: moment().format("MM-DD-YYYY hh:mm a (Edited)"),
            };
          }
          // Handle editing replies
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map((reply) =>
                reply.id === updatedComment.commentId
                  ? {
                      ...reply,
                      text: processMessageForDisplay(updatedComment.message || reply.text, updatedComment.mentions || reply.mentions), // Convert @id back to @UserName for display
                      hashtags: updatedComment.hashtags || reply.hashtags,
                      mentions: updatedComment.mentions || reply.mentions,
                      timestamp: moment().format("MM-DD-YYYY hh:mm a (Edited)"),
                    }
                  : reply
              ),
            };
          }
          return comment;
        })
      );
    }
  }, [processMessageForDisplay]);

  const handleDeleteComment = useCallback((deletedComment: any) => {
    if (!mountedRef.current) return;

    if (
      deletedComment.module === moduleRef.current &&
      deletedComment.id === moduleIdRef.current
    ) {
      setComments((prev) =>
        prev
          .map((comment) => {
            // If deleting a reply
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.filter(
                  (reply) => reply.id !== deletedComment.commentId
                ),
              };
            }
            return comment;
          })
          .filter((comment) => comment.id !== deletedComment.commentId)
      );
    }
  }, []);

  // // Socket initialization - FIXED VERSION
  // useEffect(() => {
  //   if (!moduleId) {
  //     console.warn("Module ID is undefined - skipping socket initialization");
  //     return;
  //   }

  //   const currentRoom = `${module}_${moduleId}`;

  //   let socketInstance: Socket | null = null;
  //   let hasJoinedRoom = false;

  //   try {
  //     socketInstance = getSocket();
  //   } catch (error) {
  //     console.warn("Socket not initialized yet, waiting...");
  //     return;
  //   }

  //   if (socketInstance) {
  //     console.log("Setting up socket connection for:", currentRoom);

  //     socketRef.current = socketInstance;
  //     isConnectedRef.current = socketInstance.connected;

  //     if (mountedRef.current) {
  //       setSocketStatus({
  //         connected: socketInstance.connected,
  //         initialized: true,
  //       });
  //     }

  //     // Create stable event handlers that capture current module/moduleId
  //     const stableHandleNewComment = (newComment: any) => {
  //       if (newComment.module === module && newComment.id === moduleId) {
  //         if (newComment.parentId) {
  //           setComments((prev) =>
  //             prev.map((comment) => {
  //               if (comment.id === newComment.parentId) {
  //                 return {
  //                   ...comment,
  //                   replies: [
  //                     ...(comment.replies || []),
  //                     formatSocketComment(newComment),
  //                   ],
  //                 };
  //               }
  //               return comment;
  //             })
  //           );
  //         } else {
  //           setComments((prev) => [formatSocketComment(newComment), ...prev]);
  //         }
  //       }
  //     };

  //     const stableHandleEditComment = (updatedComment: any) => {
  //       if (
  //         updatedComment.module === module &&
  //         updatedComment.id === moduleId
  //       ) {
  //         setComments((prev) =>
  //           prev.map((comment) => {
  //             if (comment.id === updatedComment.commentId) {
  //               return {
  //                 ...comment,
  //                 text: updatedComment.message || comment.text,
  //                 hashtags: updatedComment.hashtags || comment.hashtags,
  //                 mentions: updatedComment.mentions || comment.mentions,
  //                 timestamp: moment().format("MM-DD-YYYY hh:mm a (Edited)"),
  //               };
  //             }
  //             if (comment.replies) {
  //               return {
  //                 ...comment,
  //                 replies: comment.replies.map((reply) =>
  //                   reply.id === updatedComment.commentId
  //                     ? {
  //                         ...reply,
  //                         text: updatedComment.message || reply.text,
  //                         hashtags: updatedComment.hashtags || reply.hashtags,
  //                         mentions: updatedComment.mentions || reply.mentions,
  //                         timestamp: moment().format(
  //                           "MM-DD-YYYY hh:mm a (Edited)"
  //                         ),
  //                       }
  //                     : reply
  //                 ),
  //               };
  //             }
  //             return comment;
  //           })
  //         );
  //       }
  //     };

  //     const stableHandleDeleteComment = (deletedComment: any) => {
  //       if (
  //         deletedComment.module === module &&
  //         deletedComment.id === moduleId
  //       ) {
  //         setComments((prev) =>
  //           prev
  //             .map((comment) => {
  //               if (comment.replies) {
  //                 return {
  //                   ...comment,
  //                   replies: comment.replies.filter(
  //                     (reply) => reply.id !== deletedComment.commentId
  //                   ),
  //                 };
  //               }
  //               return comment;
  //             })
  //             .filter((comment) => comment.id !== deletedComment.commentId)
  //         );
  //       }
  //     };

  //     // Set up event listeners
  //     socketInstance.on("room:comment-new", stableHandleNewComment);
  //     socketInstance.on("room:comment-edit", stableHandleEditComment);
  //     socketInstance.on("room:comment-delete", stableHandleDeleteComment);

  //     // Handle connection state changes
  //     socketInstance.on("connect", () => {
  //       if (mountedRef.current) {
  //         isConnectedRef.current = true;
  //         setSocketStatus((prev) => ({ ...prev, connected: true }));
  //       }
  //     });

  //     socketInstance.on("disconnect", () => {
  //       if (mountedRef.current) {
  //         isConnectedRef.current = false;
  //         setSocketStatus((prev) => ({ ...prev, connected: false }));
  //       }
  //     });

  //     // Join room
  //     console.log("Joining room:", currentRoom);
  //     joinRoom(module, moduleId)
  //       .then(() => {
  //         hasJoinedRoom = true;
  //         console.log("Successfully joined room:", currentRoom);
  //       })
  //       .catch((error) => {
  //         console.error("Failed to join room:", error);
  //       });

  //     return () => {
  //       // Cleanup when component unmounts or dependencies change
  //       console.log("Cleaning up socket for room:", currentRoom);

  //       if (socketInstance) {
  //         socketInstance.off("room:comment-new", stableHandleNewComment);
  //         socketInstance.off("room:comment-edit", stableHandleEditComment);
  //         socketInstance.off("room:comment-delete", stableHandleDeleteComment);
  //         socketInstance.off("connect");
  //         socketInstance.off("disconnect");

  //         // Leave room only if we actually joined it
  //         if (hasJoinedRoom) {
  //           leaveRoom(module, moduleId).catch((error) => {
  //             console.error("Error leaving room:", error);
  //           });
  //         }
  //       }
  //     };
  //   }
  // }, [module, moduleId]); // CRITICAL FIX: Only include module and moduleId


  useEffect(() => {
  if (!moduleId) {
    console.warn("Module ID is undefined - skipping room management");
    return;
  }

  let isMounted = true;
  let hasJoinedRoom = false;
  const roomName = `${module}_${moduleId}`;

  const setupRoomConnection = async () => {
    try {
      console.log(`Setting up room connection for: ${roomName}`);

      // Join the room (this will wait for socket connection automatically)
      await joinRoom(module, moduleId);
      
      if (!isMounted) return;

      hasJoinedRoom = true;
      console.log(`Successfully joined room: ${roomName}`);

      // Get socket and set up event listeners
      const socket = getSocket();
      
      // Create event handlers for this specific room using refs to prevent memory leaks
      eventHandlersRef.current.handleNewComment = (newComment: any) => {
        if (!isMounted || newComment.module !== module || newComment.id !== moduleId) return;

        console.log('Received new comment:', newComment);
        
        if (newComment.parentId) {
          setComments((prev) =>
            prev.map((comment) => {
              if (comment.id === newComment.parentId) {
                return {
                  ...comment,
                  replies: [
                    ...(comment.replies || []),
                    formatSocketComment(newComment),
                  ],
                };
              }
              return comment;
            })
          );
        } else {
          setComments((prev) => [formatSocketComment(newComment), ...prev]);
        }
      };

      eventHandlersRef.current.handleEditComment = (updatedComment: any) => {
        if (!isMounted || updatedComment.module !== module || updatedComment.id !== moduleId) return;

        console.log('Received comment edit:', updatedComment);
        
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id === updatedComment.commentId) {
              const mentions = updatedComment.mentions || comment.mentions;
              const displayMessage = processMessageForDisplay(updatedComment.message || comment.text, mentions);
              return {
                ...comment,
                text: displayMessage,
                hashtags: updatedComment.hashtags || comment.hashtags,
                mentions: mentions,
                timestamp: moment().format("MM-DD-YYYY hh:mm a (Edited)"),
              };
            }
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.map((reply) =>
                  reply.id === updatedComment.commentId
                    ? {
                        ...reply,
                        text: processMessageForDisplay(updatedComment.message || reply.text, updatedComment.mentions || reply.mentions),
                        hashtags: updatedComment.hashtags || reply.hashtags,
                        mentions: updatedComment.mentions || reply.mentions,
                        timestamp: moment().format("MM-DD-YYYY hh:mm a (Edited)"),
                      }
                    : reply
                ),
              };
            }
            return comment;
          })
        );
      };

      eventHandlersRef.current.handleDeleteComment = (deletedComment: any) => {
        if (!isMounted || deletedComment.module !== module || deletedComment.id !== moduleId) return;

        console.log('Received comment delete:', deletedComment);
        
        setComments((prev) =>
          prev
            .map((comment) => {
              if (comment.replies) {
                return {
                  ...comment,
                  replies: comment.replies.filter(
                    (reply) => reply.id !== deletedComment.commentId
                  ),
                };
              }
              return comment;
            })
            .filter((comment) => comment.id !== deletedComment.commentId)
        );
      };

      // Wrapper functions to call the ref handlers
      const newCommentHandler = (data: any) => eventHandlersRef.current.handleNewComment?.(data);
      const editCommentHandler = (data: any) => eventHandlersRef.current.handleEditComment?.(data);
      const deleteCommentHandler = (data: any) => eventHandlersRef.current.handleDeleteComment?.(data);

      // Add event listeners
      socket.on("room:comment-new", newCommentHandler);
      socket.on("room:comment-edit", editCommentHandler);
      socket.on("room:comment-delete", deleteCommentHandler);

      // Update refs and status
      socketRef.current = socket;
      isConnectedRef.current = socket.connected;
      
      if (isMounted) {
        setSocketStatus({ connected: socket.connected, initialized: true });
      }

      // Store handler references for cleanup
      const cleanupHandlers = {
        newCommentHandler,
        editCommentHandler,
        deleteCommentHandler,
        socket
      };

    } catch (error) {
      console.error(`Failed to setup room connection for ${roomName}:`, error);
      if (isMounted) {
        setSocketStatus({ connected: false, initialized: true });
      }
    }
  };

  // Start the setup process
  setupRoomConnection();

  // Cleanup function
  return () => {
    console.log(`Cleaning up room connection for: ${roomName}`);
    isMounted = false;

    // Remove event listeners using proper cleanup
    if (socketRef.current) {
      // Clear event handlers from ref
      eventHandlersRef.current = {};
      
      // Remove all socket event listeners for this room
      socketRef.current.off("room:comment-new");
      socketRef.current.off("room:comment-edit"); 
      socketRef.current.off("room:comment-delete");
    }

    // Leave room if we joined it
    if (hasJoinedRoom) {
      leaveRoom(module, moduleId).catch((error) => {
        console.error(`Error leaving room ${roomName}:`, error);
      });
    }

    socketRef.current = null;
    isConnectedRef.current = false;
    setSocketStatus({ connected: false, initialized: false });
  };
}, [module, moduleId, formatSocketComment]); // Only essential dependencies

  // Data fetching functions
  const fetchTaskDropDownData = useCallback(async (projectId: number) => {
    if (!projectId || !mountedRef.current) {
      return;
    }

    try {
      const apiResponse = await tasksApis.fetchTaskDropDown({ projectId });
      const { data, status } = apiResponse;
      if (status && mountedRef.current) {
        const options = data?.map((item: any) => ({
          label: item.taskName,
          value: item.taskId,
          entityType: item.entityType,
        }));
        setTaskDropdownData(options || []);
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      if (mountedRef.current) {
        setTaskDropdownData([]);
      }
    }
  }, []);

  const fetchProjectUsers = useCallback(
    async (projectId: number) => {
      if (!projectId || !mountedRef.current) {
        return;
      }

      try {
        const response = await projectsApis.fetchProjectUsers(projectId);
        const { data: usersList, status } = response || {};
        if (status && mountedRef.current) {
          const formatted = usersList?.map((user: any) => ({
            label: user.userName,
            value: Number(user.userId),
            roleName: user.roleName,
            userDetailId: Number(user.userDetailId),
          }));
          setProjectUsers(formatted || []);
        }
      } catch (error) {
        console.error("Error fetching project users:", error);
        if (mountedRef.current) {
          messageApi.error("Failed to fetch project users");
        }
      }
    },
    [messageApi]
  );

  const fetchInitialComments = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      let response;

      switch (module) {
        case "Project":
          response = await commentsApis.fetchCommentsByProject(moduleId);
          break;
        case "Task":
          response = await commentsApis.fetchCommentsByTask(moduleId);
          break;
        case "Issue":
          response = await commentsApis.fetchCommentsByIssue(moduleId);
          break;
        default:
          throw new Error(`Unsupported module type: ${module}`);
      }

      if (response.status && mountedRef.current) {
        const flattenedComments = flattenComments(response.data);
        setComments(flattenedComments);
      } else if (mountedRef.current) {
        messageApi.error("Failed to fetch comments");
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      if (mountedRef.current) {
        messageApi.error("Error fetching comments");
      }
    }
  }, [module, moduleId, flattenComments, messageApi]);

  // Initial data loading - FIXED: Stable dependencies
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        console.warn("Project ID is undefined, skipping data loading");
        return;
      }

      setIsLoading(true);
      try {
        // Fetch task dropdown data first
        await fetchTaskDropDownData(projectId);

        // Then fetch other data in parallel
        await Promise.all([
          fetchProjectUsers(projectId),
          fetchInitialComments(),
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadData();
  }, [projectId]); // CRITICAL FIX: Only depend on projectId

  // Editor handling functions
  const handleEditorChange = useCallback(
    (
      value: string,
      delta: any,
      source: string,
      editor: any,
      mode: EditorMode
    ) => {
      const updateEditorState = (prevState: any) => {
        // Synchronize mentions and hashtags with the new text value
        const synchronized = synchronizeWithText(value, prevState.mentions, prevState.hashtags);
        
        return {
          ...prevState,
          value,
          mentions: synchronized.mentions,
          hashtags: synchronized.hashtags,
        };
      };

      switch (mode) {
        case "main":
          setMainEditor(updateEditorState);
          break;
        case "reply":
          setReplyEditor(updateEditorState);
          break;
        case "edit":
          setEditEditor(updateEditorState);
          break;
      }

      if (editor && source === "user") {
        const selection = editor.getSelection();
        if (selection && typeof selection.index === "number") {
          const cursorPosition = selection.index;
          const textBeforeCursor = editor.getText(0, cursorPosition);
          const lastAtIndex = textBeforeCursor.lastIndexOf("@");
          const lastHashIndex = textBeforeCursor.lastIndexOf("#");

          let dropdownType: "mention" | "task" | null = null;
          let searchTerm = "";
          let showDropdown = false;

          if (
            lastHashIndex !== -1 &&
            (lastAtIndex === -1 || lastHashIndex > lastAtIndex) &&
            cursorPosition > lastHashIndex
          ) {
            searchTerm = textBeforeCursor
              .slice(lastHashIndex + 1, cursorPosition)
              .trim();
            dropdownType = "task";
            showDropdown = true;
          } else if (
            lastAtIndex !== -1 &&
            (lastHashIndex === -1 || lastAtIndex > lastHashIndex) &&
            cursorPosition > lastAtIndex
          ) {
            searchTerm = textBeforeCursor
              .slice(lastAtIndex + 1, cursorPosition)
              .trim();
            dropdownType = "mention";
            showDropdown = true;
          }

          // Update dropdown state based on mode
          const dropdownUpdate = {
            showDropdown,
            dropdownType,
            searchTerm,
            dropdownPosition: { top: 0, left: 0 },
          };

          switch (mode) {
            case "main":
              setMainEditor((prev) => ({ ...prev, ...dropdownUpdate }));
              break;
            case "reply":
              setReplyEditor((prev) => ({ ...prev, ...dropdownUpdate }));
              break;
            case "edit":
              setEditEditor((prev) => ({ ...prev, ...dropdownUpdate }));
              break;
          }

          // Set dropdown position if showing
          if (showDropdown && (lastAtIndex !== -1 || lastHashIndex !== -1)) {
            const triggerIndex =
              lastHashIndex > lastAtIndex ? lastHashIndex : lastAtIndex;
            const bounds = editor.getBounds(triggerIndex);
            const editorContainer = editor.container;
            if (editorContainer && bounds) {
              const editorRect = editorContainer.getBoundingClientRect();
              const position = {
                top: bounds.bottom + editorRect.top + window.scrollY + 10,
                left: bounds.left + editorRect.left + window.scrollX,
              };

              switch (mode) {
                case "main":
                  setMainEditor((prev) => ({
                    ...prev,
                    dropdownPosition: position,
                  }));
                  break;
                case "reply":
                  setReplyEditor((prev) => ({
                    ...prev,
                    dropdownPosition: position,
                  }));
                  break;
                case "edit":
                  setEditEditor((prev) => ({
                    ...prev,
                    dropdownPosition: position,
                  }));
                  break;
              }
            }
          }
        }
      }
    },
    [synchronizeWithText]
  );

  const handleSelect = useCallback(
    (item: SelectOption, type: "mention" | "task", mode: EditorMode) => {
      let quill;
      switch (mode) {
        case "main":
          quill = quillMainRef.current?.getEditor();
          break;
        case "reply":
          quill = quillReplyRef.current?.getEditor();
          break;
        case "edit":
          quill = quillEditRef.current?.getEditor();
          break;
      }

      if (quill) {
        quill.focus();
        const selection = quill.getSelection();

        if (selection && typeof selection.index === "number") {
          const cursorPosition = selection.index;
          const textBeforeCursor = quill.getText(0, cursorPosition);
          const triggerIndex =
            type === "task"
              ? textBeforeCursor.lastIndexOf("#")
              : textBeforeCursor.lastIndexOf("@");

          if (triggerIndex !== -1) {
            quill.deleteText(triggerIndex, cursorPosition - triggerIndex);
            const displayText =
              type === "task" ? `#${item.label}` : `@${item.label}`;
            quill.insertText(triggerIndex, displayText);
            quill.setSelection(triggerIndex + displayText.length + 1);
          }
        }

        // Update the corresponding editor state with synchronization
        const updateState = (prevState: any) => {
          let newHashtags = prevState.hashtags;
          let newMentions = prevState.mentions;

          if (type === "task") {
            const newHashtag = {
              hashtag: item.label,
              entityType: (item as TaskOption).entityType || "Task",
              entityId: Number(item.value),
            };
            newHashtags = addHashtag(prevState.hashtags, newHashtag);
          }

          if (type === "mention") {
            const newMention = {
              userId: Number(item.value), // Send ID to socket
              userName: item.label, // Display name in UI
              userDetailId: (item as any).userDetailId,
            };
            newMentions = addMention(prevState.mentions, newMention);
          }

          // Synchronize with current text to ensure consistency
          const synchronized = synchronizeWithText(prevState.value, newMentions, newHashtags);

          return {
            ...prevState,
            hashtags: synchronized.hashtags,
            mentions: synchronized.mentions,
            showDropdown: false,
            dropdownType: null,
            searchTerm: "",
          };
        };

        switch (mode) {
          case "main":
            setMainEditor(updateState);
            break;
          case "reply":
            setReplyEditor(updateState);
            break;
          case "edit":
            setEditEditor(updateState);
            break;
        }
      }
    },
    []
  );

  // Comment actions with socket status check
  const handleSubmit = useCallback(
    async (mode: "main" | "reply" = "main") => {
      const editorState = mode === "main" ? mainEditor : replyEditor;

      if (!editorState.value.trim()) {
        messageApi.warning("Please enter a comment");
        return;
      }

      if (!isConnectedRef.current || !socketRef.current) {
        messageApi.error("Not connected to server. Please try again.");
        return;
      }

      const payload: NewCommentRequest = {
        projectId,
        message: processMessageForSocket(editorState.value, editorState.mentions), // Replace @UserName with @userId
        module,
        id: moduleId,
        status: "Sent",
        projectName: projectName,
        parentId:
          mode === "reply" ? replyEditor.parentId || undefined : undefined,
        hashtags: editorState.hashtags.map((tag) => ({
          hashtag: tag.hashtag,
          entityType: tag.entityType,
          entityId: tag.entityId,
        })),
        mentions: editorState.mentions.map((mention) => ({
          userId: mention.userId, // Primary ID sent to socket
          userName: mention.userName, // Name for display purposes
          userDetailId: mention.userDetailId, // Additional user detail reference
        })),
        attachments: editorState.attachedFiles,
      };

      if (module === "Task") {
        payload.taskName = name;
      } else if (module === "Issue") {
        payload.issueName = name;
      }

      try {
        // Ensure mentions are properly structured: ID for socket processing, name for display
        console.log("Original message:", editorState.value);
        console.log("Processed message (sent to socket):", payload.message);
        console.log("Sending comment with mentions:", payload.mentions);
        
        socketRef.current.emit("comment-new", payload, (ack: any) => {
          if (ack && ack.success) {
            messageApi.success(
              mode === "reply"
                ? "Reply added successfully"
                : "Comment added successfully"
            );
            if (mode === "main") {
              resetMainEditor();
            } else {
              resetReplyEditor();
            }
            setExpanded(false);
            // Refetch comments so the newly added comment + attachments
            // appear immediately even if the socket broadcast back to the
            // sender is missed.
            fetchInitialComments();
          } else {
            messageApi.error(
              ack?.message ||
                `Failed to add ${mode === "reply" ? "reply" : "comment"}`
            );
          }
        });
      } catch (error) {
        console.error(`Error submitting ${mode}:`, error);
        messageApi.error(`Error submitting ${mode}`);
      }
    },
    [
      mainEditor,
      replyEditor,
      projectId,
      module,
      moduleId,
      projectName,
      name,
      messageApi,
      resetMainEditor,
      resetReplyEditor,
      processMessageForSocket,
      fetchInitialComments,
    ]
  );

  // Edit comment functionality
  const handleEdit = useCallback((comment: Comment) => {
    setEditEditor({
      value: comment.text,
      attachedFiles: comment.files || [],
      hashtags: comment.hashtags || [],
      mentions: comment.mentions || [],
      showDropdown: false,
      dropdownType: null,
      searchTerm: "",
      dropdownPosition: { top: 0, left: 0 },
      commentId: comment.id,
      originalComment: comment,
    });

    setTimeout(() => {
      const editor = quillEditRef.current?.getEditor();
      if (editor) {
        editor.focus();
        editor.setSelection(editor.getLength());
      }
    }, 100);
  }, []);

  const handleUpdateComment = useCallback(async () => {
    if (!editEditor.originalComment || !editEditor.commentId) return;

    const editPayload: any = {
      commentId: editEditor.commentId,
      module,
      id: moduleId,
      projectId,
      projectName: projectName,
      message: processMessageForSocket(editEditor.value, editEditor.mentions), // Replace @UserName with @userId
      hashtags: editEditor.hashtags.map((tag) => ({
        hashtag: tag.hashtag,
        entityType: tag.entityType,
        entityId: tag.entityId,
      })),
      mentions: editEditor.mentions.map((mention) => ({
        userId: mention.userId, // Primary ID sent to socket
        userName: mention.userName, // Name for display purposes
        userDetailId: mention.userDetailId, // Additional user detail reference
      })),
    };

    if (module === "Task") {
      editPayload.taskName = name;
    } else if (module === "Issue") {
      editPayload.issueName = name;
    }

    try {
      if (socketRef.current && isConnectedRef.current) {
        // Ensure edit payload has proper ID/name structure for mentions
        console.log("Original edit message:", editEditor.value);
        console.log("Processed edit message (sent to socket):", editPayload.message);
        console.log("Editing comment with mentions:", editPayload.mentions);
        
        socketRef.current.emit(
          "comment-edit",
          editPayload,
          async (ack: any) => {
            if (ack && ack.success) {
              messageApi.success("Comment updated successfully");
              resetEditEditor();
              fetchInitialComments();
            } else {
              messageApi.error(ack?.message || "Failed to update comment");
            }
          }
        );
      } else {
        messageApi.error("Not connected to server. Please try again.");
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      messageApi.error("Error updating comment");
    }
  }, [
    editEditor,
    module,
    moduleId,
    projectId,
    projectName,
    name,
    messageApi,
    fetchInitialComments,
    resetEditEditor,
    processMessageForSocket,
  ]);

  // Delete comment functionality
  const handleDeleteCommentAction = useCallback(
    async (commentId: number) => {
      const deletePayload: DeleteCommentPayload = {
        commentId,
        projectId,
        module,
        id: moduleId,
        projectName: projectName,
      };

      if (module === "Task") {
        deletePayload.taskName = name;
      } else if (module === "Issue") {
        deletePayload.issueName = name;
      }

      try {
        if (socketRef.current && isConnectedRef.current) {
          setIsDeleting(true);
          socketRef.current.emit(
            "comment-delete",
            deletePayload,
            (ack: any) => {
              setIsDeleting(false);
              if (ack && ack.success) {
                messageApi.success("Comment deleted successfully");
                setDeleteConfirmId(null);
                setConfirmDeleteChecked(false);
                fetchInitialComments();
              } else {
                messageApi.error(ack?.message || "Failed to delete comment");
              }
            }
          );
        } else {
          messageApi.error("Not connected to server. Please try again.");
        }
      } catch (error) {
        setIsDeleting(false);
        console.error("Error deleting comment:", error);
        messageApi.error("Error deleting comment");
        setDeleteConfirmId(null);
        setConfirmDeleteChecked(false);
      }
    },
    [
      projectId,
      module,
      moduleId,
      projectName,
      name,
      messageApi,
      fetchInitialComments,
    ]
  );

  // View functions
  const viewEntityDetails = useCallback(
    (entityId: number, entityType: CommentEntityType, hashtagData?: any) => {
      if (entityType === "Task") {
        navigate(`/pms/project/task-detail/${entityId}`, {
          state: {
            projectId: hashtagData?.projectId || projectId,
            projectName: projectName,
            phaseId: hashtagData?.phaseId,
            activityId: hashtagData?.activityId,
            taskId: hashtagData?.taskId || entityId,
            returnPath: location.pathname,
            returnState: (location.state as any) || {},
          },
        });
      }
    },
    [navigate, location.pathname, location.state, projectId, projectName]
  );

  const viewUserDetails = useCallback(
    (userDetailId: number) => {
      dispatch(
        updateUserDetailModalSlice({
          userId: userDetailId.toString(),
          open: true,
          activeTab: "masters",
          extra: {
            projectId: projectId,
            projectName: projectName,
          },
        })
      );
    },
    [dispatch, projectId, projectName]
  );

  // Memoized render functions for better performance
  const renderCommentText = useMemo(() => 
    (
      text: string,
      hashtags: HashTagRequest[] = [],
      mentions: MentionUserRequest[] = []
    ) => {
      let processedText = text;

      // Process hashtags
      hashtags?.forEach((hashtag) => {
        if (!hashtag?.entityId) return;
        const taskLabel = taskLookupMap.get(hashtag.entityId);
        if (taskLabel) {
          const displayText = `#${taskLabel}`;
          const regex = new RegExp(
            `#${taskLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
            "g"
          );
          const hashtagData = JSON.stringify(hashtag).replace(/"/g, "&quot;");

          processedText = processedText.replace(
            regex,
            isFromWorkUI
              ? displayText
              : `<span 
          data-id="${hashtag.entityId}" 
          data-type="${hashtag.entityType || "Task"}" 
          data-hashtag="${hashtagData}"
          class="task-link" 
          style="color: #52c41a; cursor: pointer; text-decoration: underline;"
        >${displayText}</span>`
          );
        }
      });
      // Process mentions - display userName but store userId for socket operations
      mentions?.forEach((mention) => {
        const displayName = mention.userName || `User ${mention.userId}`;
        const regex = new RegExp(
          `@${displayName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
          "g"
        );
        processedText = processedText.replace(
          regex,
          isFromWorkUI
            ? `@${displayName}`
            : `<span 
          data-id="${mention.userDetailId}" 
          data-user-id="${mention.userId}"
          data-type="mention" 
          class="mention-link" 
          style="color: #1890ff; cursor: pointer; text-decoration: underline;"
        >@${displayName}</span>`
        );
      });

      return (
        <div
          style={{ margin: "5px 0" }}
          dangerouslySetInnerHTML={{ __html: processedText }}
          onClick={(e) => {
            if (isFromWorkUI) return;

            e.preventDefault();
            const target = e.target as HTMLElement;
            if (target.classList.contains("task-link")) {
              const entityId = target.getAttribute("data-id");
              const entityType = target.getAttribute(
                "data-type"
              ) as CommentEntityType;
              const hashtagData = target.getAttribute("data-hashtag");
              if (entityId && entityType) {
                viewEntityDetails(
                  Number(entityId),
                  entityType,
                  hashtagData
                    ? JSON.parse(hashtagData.replace(/&quot;/g, '"'))
                    : ""
                );
              }
            } else if (target.classList.contains("mention-link")) {
              const userId = target.getAttribute("data-id");
              if (userId) {
                viewUserDetails(Number(userId));
              }
            }
          }}
        />
      );
    },
    [taskLookupMap, isFromWorkUI, viewEntityDetails, viewUserDetails]
  );

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownListRef.current &&
        !dropdownListRef.current.contains(event.target as Node)
      ) {
        setMainEditor((prev) => ({
          ...prev,
          showDropdown: false,
          dropdownType: null,
          searchTerm: "",
        }));
        setReplyEditor((prev) => ({
          ...prev,
          showDropdown: false,
          dropdownType: null,
          searchTerm: "",
        }));
        setEditEditor((prev) => ({
          ...prev,
          showDropdown: false,
          dropdownType: null,
          searchTerm: "",
        }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render dropdown component
  const renderDropdown = (editorState: EditorState, mode: EditorMode) => {
    if (!editorState.showDropdown || !editorState.dropdownType) return null;

    const items =
      editorState.dropdownType === "mention"
        ? getFilteredMentions(editorState.searchTerm)
        : getFilteredTasks(editorState.searchTerm);

    return (
      <div
        ref={dropdownDivRef}
        style={{
          position: "absolute",
          top: editorState.dropdownPosition.top,
          left: editorState.dropdownPosition.left,
          zIndex: 1000,
          backgroundColor: "#fff",
          border: "1px solid #d9d9d9",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          width: "200px",
          maxHeight: "200px",
          overflowY: "auto",
        }}
      >
        <ul
          ref={dropdownListRef}
          style={{ listStyle: "none", margin: 0, padding: "4px 0" }}
        >
          {items.map((item) => (
            <li
              key={item.value}
              onClick={() =>
                handleSelect(item, editorState.dropdownType!, mode)
              }
              style={{
                padding: "6px 12px",
                cursor: "pointer",
                backgroundColor: "#fff",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#fff")
              }
            >
              {item.label}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px",
        }}
      >
        Loading comments...
      </div>
    );
  }

  return (
    <div>
      {contextHolder}

      {/* Socket Status Indicator (for debugging) */}
      {/* {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            padding: "5px 10px",
            borderRadius: 4,
            backgroundColor: socketStatus.connected ? "#52c41a" : "#ff4d4f",
            color: "white",
            zIndex: 1000,
            fontSize: 12,
          }}
        >
          {socketStatus.connected ? "🟢" : "🔴"} Socket{" "}
          {socketStatus.connected ? "Connected" : "Disconnected"}
          {currentRoomRef.current && <div>Room: {currentRoomRef.current}</div>}
        </div>
      )} */}

      {/* Main Comment Editor */}
      {!isFromWorkUI && !replyEditor.parentId && !editEditor.commentId && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            width: "100%",
          }}
        >
          <Avatar style={{ backgroundColor: "#ff7875", flexShrink: 0 }}>
            {getInitials(currentUser?.firstName || "")}
          </Avatar>
          <div style={{ flexGrow: 1, position: "relative" }}>
            <ReactQuill
              ref={quillMainRef}
              value={mainEditor.value}
              onChange={(value, delta, source, editor) =>
                handleEditorChange(value, delta, source, editor, "main")
              }
              placeholder="Write a comment..."
              modules={{ toolbar: toolbarOptions }}
              style={{
                minHeight: "100px",
                borderRadius: "4px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
              className={isDarkTheme ? "dark-quill-theme" : "light-quill-theme"}
              onFocus={() =>
                !expanded &&
                setMainEditor((prev) => ({ ...prev, showDropdown: false }))
              }
            />

            {renderDropdown(mainEditor, "main")}

            {!expanded && (
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "8px",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                  height: "24px",
                  lineHeight: "24px",
                }}
              >
                <Switch
                  size="small"
                  checkedChildren={<MoonOutlined />}
                  unCheckedChildren={<SunOutlined />}
                  checked={isDarkTheme}
                  onChange={setIsDarkTheme}
                />
                <Button
                  className="btn-icon"
                  size="sm"
                  onClick={() => setExpanded(true)}
                  style={{
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                    boxShadow: "none",
                    color: "inherit",
                    padding: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "24px",
                  }}
                >
                  <ExpandOutlined />
                </Button>
              </div>
            )}

            {!expanded && hasEditorContent(mainEditor.value) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  marginTop: 0,
                  padding: "8px",
                  borderTop: "1px solid #eee",
                  backgroundColor: "#f8f8f8",
                  borderRadius: "0 0 4px 4px",
                }}
              >
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleSubmit("main")}
                    disabled={!hasEditorContent(mainEditor.value)}
                  >
                    Add Comment
                  </Button>
                  <Button
                    size="sm"
                    className="btn-dim btn btn-secondary"
                    onClick={resetMainEditor}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expanded Modal */}
      <Modal
        open={expanded}
        footer={null}
        width="100vw"
        style={{
          top: 0,
          left: 0,
          margin: 0,
          padding: 0,
          height: "100vh",
        }}
        bodyStyle={{
          padding: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
        closable={false}
        maskClosable={false}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 15px",
            borderBottom: "1px solid #eee",
            backgroundColor: "#f8f8f8",
          }}
        >
          <strong>Comment Editor</strong>
          <div style={{ display: "flex", gap: "8px" }}>
            <Switch
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
              checked={isDarkTheme}
              onChange={setIsDarkTheme}
            />
            <Button
              className="btn-icon"
              size="sm"
              onClick={() => setExpanded(false)}
              style={{
                backgroundColor: "transparent",
                borderColor: "transparent",
                boxShadow: "none",
                color: "inherit",
                padding: 0,
              }}
            >
              <CompressOutlined />
            </Button>
          </div>
        </div>

        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            padding: 16,
            position: "relative",
          }}
        >
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              overflow: "hidden",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ReactQuill
              ref={quillMainRef}
              value={mainEditor.value}
              onChange={(value, delta, source, editor) =>
                handleEditorChange(value, delta, source, editor, "main")
              }
              placeholder="Write a comment..."
              modules={{ toolbar: toolbarOptions }}
              style={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
              className={isDarkTheme ? "dark-quill-theme" : "light-quill-theme"}
            />

            {renderDropdown(mainEditor, "main")}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 16,
              gap: 8,
              paddingTop: 10,
              borderTop: "1px solid #eee",
            }}
          >
            <Button
              color="primary"
              size="sm"
              onClick={() => handleSubmit("main")}
              disabled={!mainEditor.value.trim()}
            >
              Save
            </Button>
            <Button
              className="btn-dim btn btn-secondary"
              size="sm"
              onClick={() => {
                setExpanded(false);
                resetMainEditor();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reply Editor Modal */}
      {replyEditor.parentId && (
        <Modal
          open={true}
          footer={null}
          title={`Replying to ${
            comments.find((c) => c.id === replyEditor.parentId)?.user ||
            "comment"
          }`}
          onCancel={resetReplyEditor}
          width={800}
        >
          <div style={{ position: "relative" }}>
            <ReactQuill
              ref={quillReplyRef}
              value={replyEditor.value}
              onChange={(value, delta, source, editor) =>
                handleEditorChange(value, delta, source, editor, "reply")
              }
              placeholder="Write your reply..."
              modules={{ toolbar: toolbarOptions }}
              style={{ minHeight: "150px" }}
              className={isDarkTheme ? "dark-quill-theme" : "light-quill-theme"}
            />

            {renderDropdown(replyEditor, "reply")}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 16,
                gap: 8,
              }}
            >
              <Button
                color="primary"
                size="sm"
                onClick={() => handleSubmit("reply")}
                disabled={!replyEditor.value.trim()}
              >
                Post Reply
              </Button>
              <Button
                className="btn-dim btn btn-secondary"
                size="sm"
                onClick={resetReplyEditor}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Comment Modal */}
      {editEditor.commentId && (
        <Modal
          open={true}
          footer={null}
          title="Edit Comment"
          onCancel={resetEditEditor}
          width={800}
        >
          <div style={{ position: "relative" }}>
            <ReactQuill
              ref={quillEditRef}
              value={editEditor.value}
              onChange={(value, delta, source, editor) =>
                handleEditorChange(value, delta, source, editor, "edit")
              }
              placeholder="Edit your comment..."
              modules={{ toolbar: toolbarOptions }}
              style={{ minHeight: "150px" }}
              className={isDarkTheme ? "dark-quill-theme" : "light-quill-theme"}
            />

            {renderDropdown(editEditor, "edit")}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 16,
                gap: 8,
              }}
            >
              <Button
                color="primary"
                size="sm"
                onClick={handleUpdateComment}
                disabled={
                  editEditor.value === editEditor.originalComment?.text &&
                  JSON.stringify(editEditor.hashtags) ===
                    JSON.stringify(
                      editEditor.originalComment?.hashtags || []
                    ) &&
                  JSON.stringify(editEditor.mentions) ===
                    JSON.stringify(editEditor.originalComment?.mentions || [])
                }
              >
                Update Comment
              </Button>
              <Button
                className="btn-dim btn btn-secondary"
                size="sm"
                onClick={resetEditEditor}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Comments List */}
      <div className="mt-4" style={{ marginTop: 32 }}>
        {comments.map((comment, index) => {
          const isCurrentUser = comment.user === currentUser?.firstName;

          return (
            <div key={comment.id}>
              {index > 0 && <hr className="comment-divider" />}

              <div className="comment-container">
                <div
                  className="comment-main"
                  onMouseEnter={() =>
                    !isFromWorkUI && setHoveredCommentId(comment.id)
                  }
                  onMouseLeave={() =>
                    !isFromWorkUI && setHoveredCommentId(null)
                  }
                >
                  <div className="comment-avatar">
                    <Avatar style={{ backgroundColor: "#ff7875" }}>
                      {comment.initials}
                    </Avatar>
                  </div>

                  <div className="comment-content">
                    <div className="comment-header">
                      <strong>{comment.user}</strong>
                    </div>

                    <div className="comment-text">
                      {renderCommentText(
                        comment.text,
                        comment.hashtags,
                        comment.mentions
                      )}
                    </div>

                    <div className="comment-meta">
                      <span className="comment-timestamp">
                        {comment.timestamp}
                      </span>
                      {!isFromWorkUI && hoveredCommentId === comment.id && (
                        <div className="comment-actions">
                          {isCurrentUser && (
                            <>
                              <span className="action-separator">·</span>
                              <button
                                className="action-btn edit-btn"
                                onClick={() => handleEdit(comment)}
                              >
                                Edit
                              </button>
                            </>
                          )}
                          <span className="action-separator">·</span>
                          {isCurrentUser && (
                            <button
                              className="action-btn delete-btn"
                              onClick={() => setDeleteConfirmId(comment.id)}
                            >
                              Delete
                            </button>
                          )}
                          <span className="action-separator">·</span>
                          <button
                            className="action-btn reply-btn"
                            onClick={() =>
                              setReplyEditor((prev) => ({
                                ...prev,
                                parentId: comment.id,
                              }))
                            }
                          >
                            Reply
                          </button>
                        </div>
                      )}
                    </div>

                    {comment.files?.length > 0 && (
                      <div className="comment-files">
                        {comment.files.map((file, idx) => (
                          <div key={idx} className="file-attachment">
                            📎 {file.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Replies section */}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="replies-container">
                    {comment.replies.map((reply, replyIndex) => {
                      const isReplyCurrentUser =
                        reply.user === currentUser?.firstName;

                      return (
                        <div key={reply.id}>
                          {replyIndex > 0 && <hr className="reply-divider" />}

                          <div
                            className="reply-item"
                            onMouseEnter={() =>
                              !isFromWorkUI && setHoveredCommentId(reply.id)
                            }
                            onMouseLeave={() =>
                              !isFromWorkUI && setHoveredCommentId(null)
                            }
                          >
                            <div className="reply-avatar">
                              <Avatar style={{ backgroundColor: "#ff7875" }}>
                                {reply.initials}
                              </Avatar>
                            </div>

                            <div className="reply-content">
                              <div className="reply-header">
                                <strong>{reply.user}</strong>
                              </div>

                              <div className="reply-text">
                                {renderCommentText(
                                  reply.text,
                                  reply.hashtags,
                                  reply.mentions
                                )}
                              </div>

                              <div className="reply-meta">
                                <span className="reply-timestamp">
                                  {reply.timestamp}
                                </span>
                                {!isFromWorkUI &&
                                  hoveredCommentId === reply.id && (
                                    <div className="reply-actions">
                                      {isReplyCurrentUser && (
                                        <>
                                          <span className="action-separator">
                                            ·
                                          </span>
                                          <button
                                            className="action-btn edit-btn"
                                            onClick={() => handleEdit(reply)}
                                          >
                                            Edit
                                          </button>
                                        </>
                                      )}
                                      <span className="action-separator">
                                        ·
                                      </span>
                                      {isReplyCurrentUser && (
                                        <button
                                          className="action-btn delete-btn"
                                          onClick={() =>
                                            setDeleteConfirmId(reply.id)
                                          }
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  )}
                              </div>

                              {reply.files?.length > 0 && (
                                <div className="reply-files">
                                  {reply.files.map((file, idx) => (
                                    <div key={idx} className="file-attachment">
                                      📎 {file.name}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* {comments.length === 0 && (
          <div className="no-comments">
            No comments yet. {!isFromWorkUI && "Be the first to add a comment!"}
          </div>
        )} */}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmId !== null}
        onCancel={() => {
          setDeleteConfirmId(null);
          setConfirmDeleteChecked(false);
        }}
        footer={null}
        centered
      >
        <div style={{ padding: 10 }}>
          <div
            style={{
              backgroundColor: "#ff4d4f",
              color: "white",
              padding: "4px 12px",
              borderRadius: "4px",
              display: "inline-block",
              marginBottom: 12,
            }}
          >
            Delete Comment?
          </div>
          <p>This cannot be undone.</p>
          <div style={{ textAlign: "left", marginBottom: 16 }}>
            <input
              type="checkbox"
              id="confirmDelete"
              checked={confirmDeleteChecked}
              onChange={(e) => setConfirmDeleteChecked(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="confirmDelete">
              I understand this will permanently delete the comment.
            </label>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              size="sm"
              className="btn-dim btn btn-secondary"
              onClick={() => {
                setDeleteConfirmId(null);
                setConfirmDeleteChecked(false);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDeleteCommentAction(deleteConfirmId!)}
              disabled={!confirmDeleteChecked || isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* User Detail Modal */}
      {UserDetailModal?.open && (
        <ProjectInDetailModal
          visible={UserDetailModal.open}
          onClose={() => dispatch(resetUserDetailModalSlice())}
          activeTab={UserDetailModal.activeTab || "masters"}
          userData={{
            userDetailId: UserDetailModal.userId,
            ...UserDetailModal.extra,
          }}
        />
      )}
    </div>
  );
};
